import React, { useEffect, useState, useRef } from "react";
import { Button, Row, Col, Form, Card } from "react-bootstrap";
import PageTitle from "../components/common/PageTitle";
import { useTranslation } from "react-i18next";
import api from "../api/api";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { DownloadTableExcel } from "react-export-table-to-excel";
import SensorHistoryChart from "./SensorHistoryChart";
import SolarRadiationChart from "./SolarRadiationChart";
import "moment/locale/fr";
import "moment/locale/ar";
import "../css/SensorAdminPages.css";
import { getSensorDepths } from "../utils/sensorDepths";

// The device reports a BME failure as 0/0/0. Do not present that hardware
// sentinel as a weather observation in the historical table or export.
const hasInvalidBmeReading = (reading) =>
  Number(reading?.temperature) === 0 &&
  Number(reading?.humidity) === 0 &&
  Number(reading?.pressure) === 0;

const SensorHistory = () => {
  const table = useRef(null);
  const [toggle, setToggle] = useState(true);
  const [toggleView, setToggleView] = useState(true);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [codeSensor, setCodeSensor] = useState("");
  const [clicked, setClicked] = useState(false);
  const [searchVersion, setSearchVersion] = useState(0);
  const [historyData, setHistoryData] = useState([]);
  const [sensorList, setSensorList] = useState([]);
  const [dateValue, setDateValue] = useState({
    dateStart: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    dateEnd: new Date(),
  });
  const [radData, setRadData] = useState([]);
  const [dataET0, setDataET0] = useState([]);
  let { id } = useParams();
  const [sensorDepths, setSensorDepths] = useState(getSensorDepths());
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  useEffect(() => {
    const getSunRadiation = async () => {
      try {
        await api
          .get("/radiation")
          .then((response) => {
            let arr = [];
            if (response) {
              let dates = response.data.dateTime;
              let radValues = response.data.radiation;
              let dataET0 = response.data.ET0;
              for (let index = 0; index < dates.length; index++) {
                arr.push({
                  date: dates[index],
                  value: radValues[index],
                });
              }
              setDataET0(dataET0);
              setRadData(arr);
            }
          })
          .catch((err) => {
            console.error(err);
          });
      } catch (error) {
        console.error(error);
      }
    };

    const getAllSensors = async () => {
      try {
        const response = await api.get("/sensor/sensors");
        setSensorList(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error(error);
      }
    };

    getSunRadiation();
    getAllSensors();
  }, []);

  const getHistorySensor = async () => {
    if (!codeSensor) return;

    const dateStart = moment(dateValue.dateStart).format("YYYY-MM-DD");
    const dateEnd = moment(dateValue.dateEnd).format("YYYY-MM-DD");

    setIsLoadingHistory(true);
    try {
      const response = await api.get(
        `/sensor/sensor-history/${encodeURIComponent(codeSensor)}/${dateStart}/${dateEnd}`,
        { params: { all: true } }
      );
      const result = response.data || {};

      setHistoryData(Array.isArray(result.history) ? result.history : []);
      setSensorDepths(getSensorDepths(result.sensorDepths));
    } catch (error) {
      console.error(error);
      setHistoryData([]);
    } finally {
      setIsLoadingHistory(false);
    }
  };
  // Get user role from localStorage
const getUserRole = () => {
    try {
        // Try to get user object from localStorage
        const userStr = localStorage.getItem('user');
        if (userStr) {
            
            const user = JSON.parse(userStr);
            return user.role;
        }
        
    } catch (error) {
        console.error('Error getting user role:', error);
    }
    return null;
};

const isAdmin = getUserRole() === "ROLE_ADMIN";


  useEffect(() => {
    if (typeof id !== "undefined") {
      setCodeSensor(id);
    }
    if (codeSensor !== "" && clicked) {
      getHistorySensor();
    }
  }, [codeSensor, dateValue.dateEnd, dateValue.dateStart, clicked, searchVersion]);

  const handleHistorySearch = () => {
    setClicked(true);
    setSearchVersion((version) => version + 1);
  };

  const downloadHistoryData = () => {
      const historyResult = historyData;
      const exportDepths = sensorDepths;

      let csvContent =
        `Date,Temp (°C),Humidity (%),Pressure (kPa),BME status,Altitude (M),Soil Temp (°C),Humidity ${exportDepths.mv1}cm (%),Humidity ${exportDepths.mv2}cm (%),Humidity ${exportDepths.mv3}cm (%),Raw ${exportDepths.mv1}cm,Raw ${exportDepths.mv2}cm,Raw ${exportDepths.mv3}cm\r\n`;
      historyResult.forEach((history) => {
        const invalidBme = hasInvalidBmeReading(history);
        csvContent += `${history.time},${invalidBme ? "" : history.temperature},${invalidBme ? "" : history.humidity},${invalidBme ? "" : history.pressure},${invalidBme ? "BME invalid" : "valid"},${history.altitude},${history.ts},${history.mv1},${history.mv2},${history.mv3},${history.niv1},${history.niv2},${history.niv3}\r\n`;
      });

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });

      const downloadLink = document.createElement("a");
      downloadLink.href = URL.createObjectURL(blob);
      downloadLink.download = `sensor_history_${codeSensor}_${moment(
        dateValue.dateStart
      ).format("YYYYMMDD")}_${moment(dateValue.dateEnd).format(
        "YYYYMMDD"
      )}.csv`;
      downloadLink.click();
  };

  return (
    <div className="sensor-admin-page sensor-history-page p-4">
      <Row className="page-header w-100 py-4">
        <Col xs={12}>
          <div className="d-flex flex-wrap  justify-content-between align-items-center">
            <PageTitle
              sm="4"
              subtitle={t("view_sensors_data_history")}
              title={t("my_sensors_history")}
            />
            <div className="mt-3 mt-md-0">
              {" "}
              {/* Only this needs margin control */}
              <Button className="sensor-history-action" variant="info" onClick={() => navigate("/AddSensor")}>
                <i className="fas fa-plus me-2" aria-hidden="true" /> {t("add_sensor")}
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      <Row className="sensor-history-filters d-flex justify-content-between align-items-center">
        {typeof id === "undefined" ? (
          <Col lg="4" md="12" sm="12">
            <Form.Group>
              <Form.Select
                onChange={(e) => {
                  setCodeSensor(e.target.value);
                }}
                value={codeSensor}
              >
                <option value="">{t("select_sensor")}</option>
                {sensorList.map((sensor) => {
                  return (
                    <option key={sensor.code} value={sensor.code}>
                      {sensor.code}
                    </option>
                  );
                })}
              </Form.Select>
            </Form.Group>
          </Col>
        ) : (
          ""
        )}
        <Col lg="4" md="12" sm="12" className="form-group">
          <p style={{ margin: "0px" }}>{t("start_date")}</p>
          <Form.Control
            type="date"
            value={dateValue.dateStart}
            onChange={(e) => {
              setDateValue({ ...dateValue, dateStart: e.target.value });
            }}
          />
        </Col>
        <Col lg="4" md="12" sm="12" className="form-group">
          <p style={{ margin: "0px" }}>{t("end_date")}</p>
          <Form.Control
            type="date"
            value={dateValue.dateEnd}
            onChange={(e) => {
              setDateValue({ ...dateValue, dateEnd: e.target.value });
            }}
          />
        </Col>
        <Col lg="2" md="12" sm="12" className="form-group">
          <p style={{ margin: "0px" }}>{t("search")}</p>
          <Button className="sensor-history-action" onClick={handleHistorySearch}>
            <i className="fas fa-search me-2" aria-hidden="true" /> {t("search")}
          </Button>
        </Col>
      </Row>
      <Row>
        <Col lg="12" md="12" sm="12">
          <Card className="sensor-admin-card sensor-history-card">
            <Card.Header className="border-bottom">
              {codeSensor !== "" ? (
                <span>
                  {t("sensor_code")} : {codeSensor}
                </span>
              ) : null}
            </Card.Header>
            <Card.Body>
              <Button className="sensor-history-action" onClick={() => setToggleView(!toggleView)}>
                {toggleView
                  ? t("view_solar_radiation")
                  : t("view_sensor_history")}
              </Button>{" "}
              {toggleView ? (
                <Row>
                  <Col lg="12" md="12" sm="12">
                    {historyData.length > 0 ? (
                      <div className="sensor-history-controls d-flex flex-wrap gap-2 mt-3 justify-content-md-end justify-content-start">
                        <DownloadTableExcel
                          filename="history table"
                          sheet="history"
                          currentTableRef={table.current}
                        >
                          <Button variant="info">
                            <i className="fas fa-download me-2" aria-hidden="true" />
                            {t("download_all_data", { defaultValue: "Download all data" })}
                          </Button>
                        </DownloadTableExcel>
                        <Button variant="info" onClick={downloadHistoryData}>
                          <i className="fas fa-download me-2" aria-hidden="true" />
                          {t("download_history", { defaultValue: "Download History" })}
                        </Button>
                        <Button onClick={() => setToggle(!toggle)}>
                          {toggle ? t("view_chart") : t("view_table")}
                        </Button>
                      </div>
                    ) : null}
                    {isLoadingHistory ? (
                      <p className="text-center text-muted mt-4" role="status" aria-live="polite">
                        {t("loading")}
                      </p>
                    ) : clicked && historyData.length === 0 ? (
                      <p className="text-center text-muted mt-4">
                        {t("no_data", { defaultValue: "No sensor history found for this period." })}
                      </p>
                    ) : null}
                    <div
                      id="table-wrapper"
                      className={`${toggle ? "" : "d-none"}`}
                    >
                    <div
                      id="table-scroll"
                      className="sensor-history-table-wrap"
                      >
                        <table
                          ref={table}
                          className="table mb-4 table-hover table-bordered tabel-responsive-lg"
                        >
                          <thead className="bg-light text-center">
                            <tr>
                              <th
                                style={{ fontSize: 12 }}
                                scope="col"
                                className="border-0"
                              >
                                {t("Date")} (GMT)
                              </th>
                              <th
                                style={{ fontSize: 12 }}
                                scope="col"
                                className="border-0"
                              >
                                {t("Temp.")} (°C)
                              </th>
                              <th
                                style={{ fontSize: 12 }}
                                scope="col"
                                className="border-0"
                              >
                                {t("Humidity")} (%)
                              </th>
                              <th
                                style={{ fontSize: 12 }}
                                scope="col"
                                className="border-0"
                              >
                                {t("Pressure")} (kPa)
                              </th>
                              <th
                                style={{ fontSize: 12 }}
                                scope="col"
                                className="border-0"
                              >
                                {t("Altitude")} (M)
                              </th>
                              <th
                                style={{ fontSize: 12 }}
                                scope="col"
                                className="border-0"
                              >
                                {t("subsoil_Temp.")} (°C)
                              </th>
                              <th
                                style={{ fontSize: 12 }}
                                scope="col"
                                className="border-0"
                              >
                                {t("Humidity")} {sensorDepths.mv1}cm(%)
                              </th>
                              <th
                                style={{ fontSize: 12 }}
                                scope="col"
                                className="border-0"
                              >
                                {t("Humidity")} {sensorDepths.mv2}cm(%)
                              </th>
                              <th
                                style={{ fontSize: 12 }}
                                scope="col"
                                className="border-0"
                              >
                                {t("Humidity")} {sensorDepths.mv3}cm(%)
                              </th>
                              <th
                                    style={{ fontSize: 12 }}
                                    scope="col"
                                    className="border-0"
                                  >
                                    {t("Reading_time")}
                                  </th>

                              {/* Admin-only columns */}
                              {isAdmin && (
                                <>
                                  <th
                                    style={{ fontSize: 12 }}
                                    scope="col"
                                    className="border-0"
                                  >
                                    {t("Humidity")} {sensorDepths.mv1}cm ({t("raw", { defaultValue: "raw" })})
                                  </th>
                                  <th
                                    style={{ fontSize: 12 }}
                                    scope="col"
                                    className="border-0"
                                  >
                                    {t("Humidity")} {sensorDepths.mv2}cm ({t("raw", { defaultValue: "raw" })})
                                  </th>
                                  <th
                                    style={{ fontSize: 12 }}
                                    scope="col"
                                    className="border-0"
                                  >
                                    {t("Humidity")} {sensorDepths.mv3}cm ({t("raw", { defaultValue: "raw" })})
                                  </th>
                                  <th
                                    style={{ fontSize: 12 }}
                                    scope="col"
                                    className="border-0"
                                  >
                                    {t("charge")}
                                  </th>
                                  <th
                                    style={{ fontSize: 12 }}
                                    scope="col"
                                    className="border-0"
                                  >
                                    {t("Voltage adc")}
                                  </th>
                                
                                </>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {historyData &&
                              historyData.map((sensorData, index) => {
                                const invalidBme = hasInvalidBmeReading(sensorData);
                                let press = Number(sensorData.pressure) / 1000;
                                let subSoilTemp = "-";
                                if (sensorData.ts !== "-127.000") {
                                  subSoilTemp = sensorData.ts;
                                }

                                return (
                                  <tr key={index}>
                                    <td>{sensorData.time}</td>
                                    <td title={invalidBme ? t("bme_sensor_invalid", { defaultValue: "BME invalid" }) : undefined}>
                                      {invalidBme ? "—" : sensorData.temperature}
                                    </td>
                                    <td title={invalidBme ? t("bme_sensor_invalid", { defaultValue: "BME invalid" }) : undefined}>
                                      {invalidBme ? "—" : sensorData.humidity}
                                    </td>
                                    <td title={invalidBme ? t("bme_sensor_invalid", { defaultValue: "BME invalid" }) : undefined}>
                                      {invalidBme ? "—" : parseFloat(press).toFixed(2)}
                                    </td>
                                    <td>{sensorData.altitude}</td>
                                    <td className="text-center">
                                      {subSoilTemp}
                                    </td>
                                    <td>{sensorData.mv1}</td>
                                    <td>{sensorData.mv2}</td>
                                    <td>{sensorData.mv3}</td>
                                    <td>
  {sensorData.timestamp_arduino !== null && sensorData.timestamp_arduino !== "null" 
    ? moment(sensorData.timestamp_arduino).format('YYYY-MM-DD HH:mm')
    : sensorData.time ? sensorData.time.split(' ')[1] : '-'}
</td>

                                    {/* Admin-only cells */}
                                    {isAdmin && (
                                      <>
                                        <td>{sensorData.niv1}</td>
                                        <td>{sensorData.niv2}</td>
                                        <td>{sensorData.niv3}</td>
                                        <td>{Number(sensorData.charge)}</td>
                                        <td>{Number(sensorData.adc) * -1}</td>
                                  
                                      </>
                                    )}
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    {/* Pagination removed: the API now returns the complete history table. */}
                  </Col>
                  <Col
                    className={`${toggle ? "d-none" : ""}`}
                    lg="12"
                    md="8"
                    sm="8"
                  >
                    <SensorHistoryChart data={historyData} sensorDepths={sensorDepths} />
                  </Col>
                </Row>
              ) : (
                <Row>
                  <Col lg="12" md="12" sm="12">
                    <div className="sensor-history-controls d-flex justify-content-end">
                      <DownloadTableExcel
                        filename="history table"
                        sheet="history"
                        currentTableRef={table.current}
                      >
                        <Button variant="info" title={t("download", { defaultValue: "Download" })} aria-label={t("download", { defaultValue: "Download" })}>
                          <i className="fas fa-download" aria-hidden="true" />
                        </Button>
                      </DownloadTableExcel>
                      <Button onClick={() => setToggle(!toggle)}>
                        {toggle ? t("view_chart") : t("view_table")}
                      </Button>
                    </div>
                    <div
                      id="table-wrapper"
                      className={`${toggle ? "" : "d-none"}`}
                    >
                      <h5 className="text-center">
                        {t("week_solar_radiation")}
                      </h5>
                      <div id="table-scroll">
                        <table
                          ref={table}
                          className="table mb-4 table-hover table-bordered  tabel-responsive-lg "
                          style={{ overflowX: "scroll" }}
                        >
                          <thead className="bg-light text-center">
                            <tr>
                              <th scope="col" className="border-0">
                                {t("Date")}
                              </th>
                              <th scope="col" className="border-0">
                                {t("Solar_Radiation")}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {radData &&
                              radData.map((data, index) => {
                                let date = moment(data.date)
                                  .locale(
                                    i18n.language === "ar"
                                      ? "ar"
                                      : i18n.language === "fr"
                                      ? "fr"
                                      : "en"
                                  )
                                  .format("ddd, h a");
                                return (
                                  <tr key={data.date || index}>
                                    <td>{date}</td>
                                    <td>{data.value}</td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </Col>
                  <Col
                    className={`${toggle ? "d-none" : ""}`}
                    lg="12"
                    md="8"
                    sm="8"
                  >
                    <SolarRadiationChart data={radData} />
                  </Col>
                </Row>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default SensorHistory;
